import React, { useEffect, useRef, useState } from "react";
import { Box, Chip, IconButton, InputBase, Typography, useTheme } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import {
  ONBOARDING_INPUT_RADIUS,
} from "../fieldStyles";
import {
  CHAT_EASE,
  getChatSuggestionChipSx,
  getChatVisualTokens,
} from "./chatVisual";

const suggestionLabel = (suggestion) =>
  typeof suggestion === "string" ? suggestion : suggestion?.label || "";

// Server location extents arrive as "COUNTY: Hampden County (MA)"; the extent
// type is an internal discriminator, so hide it from the chip (#647, ALP-6).
// Display only: the raw label stays the selection key and the submitted value.
const EXTENT_PREFIX = /^(?:COUNTY|MSA|STATE|CITY|COUNTRY|REGION):\s*/i;
export const suggestionDisplayLabel = (suggestion) => suggestionLabel(suggestion).replace(EXTENT_PREFIX, "");

/**
 * Onboarding chat composer: suggestions, text input, send, and voice.
 */
export default function ChatComposer({
  placeholder = "Type your answer…",
  suggestions = [],
  selection = "text",
  pendingSelection = [],
  onToggleSuggestion,
  onSubmit,
  disabled = false,
  listening = false,
  onToggleVoice,
  draftValue = "",
  onDraftChange,
  requireDraft = false,
  preserveDraftOnSubmit = false,
  /** "hero" = centered pill start state; "docked" = bottom composer */
  layout = "docked",
}) {
  const theme = useTheme();
  const tokens = getChatVisualTokens(theme);
  const { composer } = tokens;
  const isHero = layout === "hero";
  const [value, setValue] = useState(draftValue);
  const inputRef = useRef(null);

  useEffect(() => {
    setValue(draftValue);
  }, [draftValue]);

  useEffect(() => {
    if (!disabled) {
      inputRef.current?.focus?.();
    }
  }, [disabled, placeholder]);

  const handleChange = (event) => {
    const next = event.target.value;
    setValue(next);
    onDraftChange?.(next);
  };

  const canSend =
    !disabled && (!requireDraft || String(value || "").trim().length > 0) &&
    (String(value || "").trim().length > 0 ||
      (selection === "multiple" && pendingSelection.length > 0) ||
      selection === "text");

  const submit = () => {
    if (!canSend) return;
    const payload =
      selection === "multiple" && pendingSelection.length > 0 && !String(value || "").trim()
        ? pendingSelection.join(", ")
        : value;
    onSubmit?.(payload);
    if (!preserveDraftOnSubmit) {
      setValue("");
      onDraftChange?.("");
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <Box
      data-testid="onboarding-chat-composer"
      data-layout={layout}
      sx={{ width: "100%" }}
    >
      {suggestions.length > 0 ? (
        <Box
          data-testid="onboarding-chat-suggestions"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: isHero ? "center" : "flex-start",
            columnGap: isHero ? 1.15 : 0.9,
            rowGap: isHero ? 1.15 : 0.9,
            pb: "1px",
            mb: isHero ? { xs: 5, md: 6.5 } : 1.75,
            maxHeight: isHero ? { xs: 140, md: 156 } : { xs: 132, md: 148 },
            overflowY: "auto",
            pr: 0.5,
            maskImage:
              suggestions.length > 12
                ? "linear-gradient(180deg, #000 78%, transparent 100%)"
                : "none",
            WebkitMaskImage:
              suggestions.length > 12
                ? "linear-gradient(180deg, #000 78%, transparent 100%)"
                : "none",
          }}
        >
          {suggestions.map((suggestion) => {
            const label = suggestionLabel(suggestion);
            const selected =
              selection === "multiple" ? pendingSelection.includes(label) : false;
            return (
              <Chip
                key={label}
                label={suggestionDisplayLabel(suggestion)}
                icon={suggestion?.iconSrc ? <Box component="img" alt="" src={suggestion.iconSrc} sx={{ width: 16, height: 16, objectFit: "contain" }} /> : undefined}
                clickable
                disabled={disabled}
                onClick={() => onToggleSuggestion?.(suggestion)}
                data-testid={`onboarding-suggestion-${label}`}
                aria-pressed={selected}
                sx={getChatSuggestionChipSx(theme, { selected, isHero })}
              />
            );
          })}
        </Box>
      ) : null}

      {listening ? (
        <Box
          data-testid="onboarding-chat-listening"
          sx={{
            mb: 1.25,
            px: 1.5,
            py: 1,
            borderRadius: "14px",
            bgcolor: tokens.CHAT_USER_BUBBLE_BG,
            border: tokens.CHAT_USER_BUBBLE_BORDER,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: tokens.CHAT_PRIMARY,
              animation: "onboardingMicPulse 1.1s ease-in-out infinite",
              "@keyframes onboardingMicPulse": {
                "0%, 100%": { transform: "scale(1)", opacity: 1 },
                "50%": { transform: "scale(1.35)", opacity: 0.55 },
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.CHAT_USER_TEXT }}>
            Listening… speak your answer
          </Typography>
        </Box>
      ) : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          minHeight: isHero ? 58 : 54,
          px: isHero ? 1.5 : 1.25,
          py: isHero ? 0.65 : 0.75,
          borderRadius: isHero ? "999px" : ONBOARDING_INPUT_RADIUS,
          border: composer.surfaceBorder,
          bgcolor: composer.surfaceBg,
          boxShadow: isHero ? composer.surfaceShadowHero : composer.surfaceShadowDocked,
          transition: `border-color 180ms ${CHAT_EASE}, box-shadow 180ms ${CHAT_EASE}, border-radius 420ms ${CHAT_EASE}`,
          "&:focus-within": {
            borderColor: composer.focusBorderColor,
            boxShadow: composer.focusShadow,
          },
        }}
      >
        <InputBase
          inputRef={inputRef}
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          multiline={!isHero}
          maxRows={isHero ? 1 : 4}
          fullWidth
          inputProps={{
            "data-testid": "onboarding-chat-input",
            "aria-label": "Answer",
          }}
          sx={{
            flex: 1,
            alignSelf: "center",
            fontSize: 15,
            fontWeight: 500,
            color: composer.inputColor,
            py: 0.5,
            px: isHero ? 0.75 : 0.25,
            "& textarea::placeholder, & input::placeholder": {
              color: composer.placeholderColor,
              opacity: 1,
            },
          }}
        />

        <IconButton
          type="button"
          aria-label={listening ? "Stop voice input" : "Start voice input"}
          data-testid="onboarding-chat-mic"
          onClick={onToggleVoice}
          disabled={disabled}
          sx={{
            width: 36,
            height: 36,
            color: listening ? tokens.CHAT_PRIMARY : composer.micColor,
            bgcolor: listening ? tokens.CHAT_USER_BUBBLE_BG : "transparent",
            flexShrink: 0,
            transition: `background-color 160ms ${CHAT_EASE}, color 160ms ${CHAT_EASE}`,
            "&:hover": {
              bgcolor: tokens.CHAT_USER_BUBBLE_BG,
              color: tokens.CHAT_PRIMARY,
            },
          }}
        >
          {listening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
        </IconButton>

        <IconButton
          type="button"
          aria-label="Send answer"
          data-testid="onboarding-chat-send"
          onClick={submit}
          disabled={!canSend}
          sx={{
            width: 38,
            height: 38,
            bgcolor: tokens.CHAT_PRIMARY,
            color: "#FFFFFF",
            flexShrink: 0,
            transition: `background-color 160ms ${CHAT_EASE}, transform 160ms ${CHAT_EASE}`,
            "&:hover": {
              bgcolor: tokens.CHAT_PRIMARY_HOVER,
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&.Mui-disabled": {
              bgcolor: composer.sendDisabledBg,
              color: "rgba(255,255,255,0.9)",
              transform: "none",
            },
          }}
        >
          <ArrowUpwardIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
